import { useEffect, useState } from "react";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { EmptyState } from "@/components/base/EmptyState";
import { devicesService } from "@/services";
import type { StaffUser, UserDevice } from "@/services/types";
import { formatDateTime } from "@/utils/format";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";

interface UserDevicesModalProps {
  user: StaffUser | null;
  open: boolean;
  onClose: () => void;
}

export function UserDevicesModal({ user, open, onClose }: UserDevicesModalProps) {
  const toast = useToast();
  const confirm = useConfirm();

  const [devices, setDevices] = useState<UserDevice[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await devicesService.getUserDevices(user.id);
      setDevices(data);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user) {
      load();
    } else {
      setDevices(null);
    }
  }, [open, user]);

  const handleRevoke = async (device: UserDevice) => {
    const ok = await confirm({
      title: "Revoke IP authorization?",
      message: `Revoking this IP will immediately log out ${user?.name} and block future logins from this IP until re-approved.`,
      confirmLabel: "Revoke IP",
      destructive: true,
    });
    if (!ok) return;

    setRevokingId(device.id);
    try {
      await devicesService.revokeDevice(device.id, "Revoked by Administrator");
      toast.success("IP Revoked", "The IP authorization was revoked.");
      await load();
    } catch (err) {
      toast.error("Could not revoke IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setRevokingId(null);
    }
  };

  const handleDelete = async (device: UserDevice) => {
    const ok = await confirm({
      title: "Delete IP record?",
      message: `Permanently delete IP record ${device.ipAddress} for ${user?.name}? If deleted, any active session from this IP will be ended, and the user can request approval again afresh.`,
      confirmLabel: "Delete IP",
      destructive: true,
    });
    if (!ok) return;

    setDeletingId(device.id);
    try {
      await devicesService.deleteDevice(device.id);
      toast.success("IP Deleted", "The IP record has been permanently deleted.");
      await load();
    } catch (err) {
      toast.error("Could not delete IP", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const approvedDevices = (devices ?? []).filter((d) => d.status === "APPROVED");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user ? `Authorized IP Access — ${user.name}` : "Authorized IP Access"}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-xs">
        <div className="flex items-center justify-between rounded-lg border border-background-200 bg-background-100 p-3">
          <div>
            <div className="font-semibold text-foreground-900">Authorized IP Addresses</div>
            <div className="text-[11px] text-foreground-500">
              Users can authorize up to 1 active IP address. Approving a new IP automatically revokes previous access.
            </div>
          </div>
          <div className="text-right">
            <span className="text-sm font-bold text-foreground-900">{approvedDevices.length} / 1</span>
            <span className="block text-[10px] uppercase tracking-wider text-foreground-400">Active Slots</span>
          </div>
        </div>

        {loading && !devices ? (
          <div className="flex h-32 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
          </div>
        ) : !devices || devices.length === 0 ? (
          <div className="py-6">
            <EmptyState
              title="No IP addresses authorized"
              description="This user has not signed in from any authorized IP address yet."
              icon="ri-router-line"
            />
          </div>
        ) : (
          <div className="space-y-2">
            {devices.map((device) => {
              const isApproved = device.status === "APPROVED";
              const isPending = device.status === "PENDING";
              const isRejected = device.status === "REJECTED";

              return (
                <div
                  key={device.id}
                  className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-background-300"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-base text-primary-600">
                      <i className="ri-router-line" />
                    </span>
                    <div className="space-y-0.5">
                      <div className="font-mono text-xs font-semibold text-foreground-900">
                        {device.ipAddress || "—"}
                      </div>
                      <div className="text-[11px] text-foreground-600">
                        {device.deviceLabel || `${device.browser || "Browser"} on ${device.os || "Device"}`}
                      </div>
                      <div className="text-[10px] text-foreground-400">
                        {device.browser} • {device.os}
                      </div>
                      {device.lastUsedAt ? (
                        <div className="text-[10px] text-foreground-400">
                          Last active: {formatDateTime(device.lastUsedAt)}
                        </div>
                      ) : (
                        <div className="text-[10px] text-foreground-400">
                          Requested: {formatDateTime(device.requestedAt)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isApproved ? (
                      <Badge variant="success">Approved</Badge>
                    ) : isPending ? (
                      <Badge variant="warning">Pending</Badge>
                    ) : isRejected ? (
                      <Badge variant="danger">Rejected</Badge>
                    ) : (
                      <Badge variant="neutral">Revoked</Badge>
                    )}

                    {isApproved ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={revokingId === device.id}
                        onClick={() => handleRevoke(device)}
                      >
                        Revoke
                      </Button>
                    ) : null}

                    <Button
                      variant="outline"
                      size="sm"
                      iconOnly
                      loading={deletingId === device.id}
                      onClick={() => handleDelete(device)}
                      title="Delete IP Record"
                      className="border-danger-200 text-danger-600 hover:bg-danger-50 hover:border-danger-300"
                      icon={<i className="ri-delete-bin-line text-sm" />}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
